// SigWaz shared TypeScript types

export interface LevelMap {
  informational: number;
  low: number;
  medium: number;
  high: number;
  critical: number;
}

export interface ConversionConfig {
  ruleIdStart: number;
  levels: LevelMap;
  noFullLog: boolean;
  emailAlert: boolean;
  emailLevels: string[];
  fieldMapOverrides: Record<string, Record<string, string>>;
  ifSidOverrides: Record<string, string>;
  ifGroupOverrides: Record<string, string>;
  rulesLinkBase: string;
  splitSize: number;
  // Rule filters
  excludedStatuses: string[];   // e.g. ["experimental","deprecated"]
  minLevel: string;             // "" = all; "medium" = skip info+low
  allowedProducts: string[];    // [] = all; ["windows"] = windows only
}

export const DEFAULT_CONFIG: ConversionConfig = {
  ruleIdStart: 900000,
  levels: { informational: 5, low: 7, medium: 10, high: 12, critical: 15 },
  noFullLog: true,
  emailAlert: false,
  emailLevels: ["critical", "high"],
  fieldMapOverrides: {},
  ifSidOverrides: {},
  ifGroupOverrides: {},
  rulesLinkBase: "https://github.com/SigmaHQ/sigma/tree/master/rules",
  splitSize: 50,
  excludedStatuses: [],
  minLevel: "",
  allowedProducts: [],
};

export const LEVEL_MAP: Record<string, number> = {
  informational: 5,
  low: 7,
  medium: 10,
  high: 12,
  critical: 15,
};

export interface SkipDetail {
  reason: string;
  detail: string;
}

export interface RuleResult {
  sigma_id: string;
  sigma_title: string;
  sigma_level: string;
  sigma_status: string;
  xml: string;
  rule_count: number;
  rule_ids: number[];
  mitre_techniques: string[];
  tactics: string[];
  skipped: SkipDetail[];
  errors: string[];
  warnings: string[];
}

export interface ConvertResponse {
  result: RuleResult;
  valid: boolean;
  validation_errors: string[];
  validation_warnings: string[];
  id_range_start: number | null;
  id_range_end: number | null;
}

export interface BatchConvertResponse {
  results: RuleResult[];
  merged_xml: string;
  xml_files: string[];
  total_rules: number;
  total_sigma: number;
  total_skipped: number;
  total_errors: number;
  mitre_techniques: string[];
  tactics: string[];
  valid: boolean;
  validation_errors: string[];
  validation_warnings: string[];
}

export type StatusKind = "idle" | "converting" | "success" | "error";

export interface AppStatus {
  kind: StatusKind;
  message?: string;
}

// Design theme variants
export type ThemeVariant = "glacier" | "midnight" | "obsidian" | "nebula";

export interface TweakSettings {
  variant: ThemeVariant;
  accent: string;
  accent2: string;
  font: string;
  btnStyle: "gradient" | "solid" | "outline" | "ghost";
  radius: "sharp" | "rounded" | "pill";
  glow: boolean;
  aurora: boolean;
  dotGrid: boolean;
  codeFontSize: number;
  lightMode: boolean;
}

export const DEFAULT_TWEAKS: TweakSettings = {
  variant: "glacier",
  accent: "#0ea5e9",
  accent2: "#67e8f9",
  font: "Inter",
  btnStyle: "gradient",
  radius: "rounded",
  glow: true,
  aurora: true,
  dotGrid: true,
  codeFontSize: 12,
  lightMode: false,
};
